/**
 * Authentication service
 */

import { API_ENDPOINTS } from '../constants';
import { Company } from './company.service';
import { get, post } from 'api/axios-client';
import { User } from './user.service';
import logger from 'core/utils/logger';

export interface LoginRequest {
  companyIdentity: string;
  databaseIdentity: string;
  username: string;
  password: string;
  requestFor: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  success: boolean;
  message: string;
  auditLogsApiEndpoint: string; // URL for subsequent API calls after authentication
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      logger.info('Attempting login', {
        companyId: credentials.companyIdentity,
        databaseId: credentials.databaseIdentity,
        username: credentials.username,
      });

      // Debug: Log the full URL being called
      const fullUrl = `${process.env.REACT_APP_AUTHENTICATION_API}/AspenAuthentication`;
      //console.log('🔐 Login URL:', fullUrl);

      const response = await post<LoginResponse>(fullUrl, credentials);

      if (response.data) {
        // Store the backend service URL for future API calls
        if (response.data.auditLogsApiEndpoint) {
          localStorage.setItem(
            'auditLogsApi',
            response.data.auditLogsApiEndpoint
          );
          logger.info('Backend service URL stored', {
            backendServiceUrl: response.data.auditLogsApiEndpoint,
          });
        }

        return response.data;
      } else {
        throw new Error(response.data || 'Login failed');
      }
    } catch (error) {
      logger.error(
        'Login failed',
        {
          companyId: credentials.companyIdentity,
          databaseId: credentials.databaseIdentity,
          username: credentials.username,
        },
        error as Error
      );
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      logger.info('Logging out user');

      //await post(API_ENDPOINTS.AUTH.LOGOUT);

      // Clear cached data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('queueAPI');
      localStorage.removeItem('auditLogsApi');
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('theme-storage');

      logger.info('Logout successful');
    } catch (error) {
      logger.error('Logout failed', {}, error as Error);
      // Still clear local storage even if API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('queueAPI');
      localStorage.removeItem('auditLogsApi');
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('theme-storage');
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await post<{ token: string }>(
        API_ENDPOINTS.AUTH.REFRESH,
        { refreshToken }
      );

      if (response.data) {
        localStorage.setItem('auth_token', response.data.token);
        logger.info('Token refreshed successfully');
        return response.data.token;
      } else {
        throw new Error(response.data || 'Token refresh failed');
      }
    } catch (error) {
      logger.error('Token refresh failed', {}, error as Error);
      throw error;
    }
  }

  async getProfile(): Promise<User> {
    try {
      const response = await get<User>(API_ENDPOINTS.AUTH.PROFILE);
      return response.data;
    } catch (error) {
      logger.error('Get profile failed', {}, error as Error);
      throw error;
    }
  }

  async getCompany(searchString: string): Promise<Company[] | null> {
    try {
      logger.info('Fetching companies', { searchString });

      // Debug: Log the full URL being called
      const fullUrl = `${process.env.REACT_APP_AUTHENTICATION_API}Companies/GetCompanyByCompanyName?name=${searchString}`;
      //console.log('🏢 Company Search URL:', fullUrl);

      const response = await get<Company[] | null>(
        `Companies/GetCompanyByCompanyName?name=${searchString}`
      );

      logger.info('Companies fetched successfully', {
        count: response.data?.length || 0,
      });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to fetch companies',
        { searchString },
        error as Error
      );
      throw error;
    }
  }
}

export const authService = new AuthService();
export default authService;
